import { useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { QuotePromotion } from '@/api/entitlements';
import { promotionsApi, type PromotionPreview } from '@/api/promotions';
import { bpToPercentText, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { promoAppliedKey, promoReasonKey } from './promotionCopy';

interface PromoCodeFieldProps {
  applied: PromotionPreview | null;
  onApplied: (preview: PromotionPreview | null) => void;
  error: string | null;
  /** The code sold out at purchase (`409 EXHAUSTED`): offer to continue without it. */
  onContinueWithout?: (() => void) | undefined;
  /** Served quote promotion (F21). Drives the status line once the quote is ready. */
  quotePromotion: QuotePromotion | null;
  quoteReady: boolean;
}

export function PromoCodeField({
  applied,
  onApplied,
  error,
  onContinueWithout,
  quotePromotion,
  quoteReady,
}: PromoCodeFieldProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [code, setCode] = useState(applied?.code ?? '');
  const [busy, setBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  async function handleApply() {
    const trimmed = code.trim();
    if (trimmed.length === 0) {
      onApplied(null);
      setPreviewError(null);
      return;
    }
    setBusy(true);
    setPreviewError(null);
    try {
      const preview = await promotionsApi.preview(trimmed);
      if (preview.redeemable) {
        onApplied(preview);
        setCode(preview.code);
        setPreviewError(null);
      } else {
        onApplied(null);
        setPreviewError(t(promoReasonKey(preview.reason)));
      }
    } catch {
      onApplied(null);
      setPreviewError(t('promo.reason.other'));
    } finally {
      setBusy(false);
    }
  }

  function handleRemove() {
    setCode('');
    setPreviewError(null);
    onApplied(null);
  }

  function handleChange(next: string) {
    setCode(next.slice(0, 32));
    if (applied) onApplied(null);
    setPreviewError(null);
  }

  const status = statusLine(t, locale, applied, quotePromotion, quoteReady, previewError);

  return (
    <Stack spacing={1}>
      <Typography variant="eyebrow" component="h3">
        {t('promo.code')}
      </Typography>
      <Stack
        spacing={1}
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ alignItems: { sm: 'flex-start' } }}
      >
        <TextField
          value={code}
          onChange={(event) => handleChange(event.target.value)}
          placeholder={t('plan.promoPlaceholder')}
          slotProps={{
            htmlInput: {
              maxLength: 32,
              autoCapitalize: 'characters',
              'aria-label': t('promo.code'),
            },
          }}
          fullWidth
          error={error !== null || previewError !== null}
          helperText={error ?? previewError ?? undefined}
        />
        {applied ? (
          <Button variant="outlined" disabled={busy} onClick={handleRemove}>
            {t('plan.remove')}
          </Button>
        ) : (
          <Button variant="outlined" disabled={busy} onClick={() => void handleApply()}>
            {t('promo.apply')}
          </Button>
        )}
      </Stack>
      {onContinueWithout !== undefined && (
        <Box>
          <Button
            variant="outlined"
            onClick={() => {
              setCode('');
              setPreviewError(null);
              onContinueWithout();
            }}
          >
            {t('promo.continueWithout')}
          </Button>
        </Box>
      )}
      <Box role="status" aria-live="polite">
        <Typography variant="body2" color="text.secondary">
          {status.text}
        </Typography>
      </Box>
    </Stack>
  );
}

function statusLine(
  t: TFunction,
  locale: string,
  applied: PromotionPreview | null,
  quotePromotion: QuotePromotion | null,
  quoteReady: boolean,
  previewError: string | null,
): { text: string } {
  if (previewError != null) return { text: previewError };
  if (applied == null) return { text: t('plan.promoNone') };
  if (!quoteReady || quotePromotion == null) {
    return { text: t('plan.promoChecking', { code: applied.code }) };
  }
  if (!quotePromotion.redeemable) {
    return { text: t(promoReasonKey(quotePromotion.reason)) };
  }
  const kind = quotePromotion.kind ?? applied.kind;
  const terms =
    kind == null
      ? applied.name || applied.code
      : t(promoAppliedKey(kind), {
          name: applied.name || applied.code,
          count:
            quotePromotion.trialDays ??
            quotePromotion.statements ??
            applied.trialDays ??
            applied.statements ??
            0,
          percent: applied.percentBp != null ? bpToPercentText(applied.percentBp, locale) : '',
          amount:
            applied.amountMinor != null && applied.currency
              ? formatMoneyMinor(applied.amountMinor, applied.currency, locale)
              : '',
        });
  return { text: t('plan.promoStatus', { code: quotePromotion.code, terms }) };
}
